// src/components/dashboard/ModelsManagement.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { Trash, AddIcon, EditIcon, TrashWhite } from "../../assets/icons/icons";
import Pagination from "../section/Pagination";
import CustomAlert from "../CustomAlert";

export default function ModelsManagement() {
  const [models, setModels] = useState([]);
  const [brands, setBrands] = useState([]);
  const [brandsById, setBrandsById] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState({});
  const [selectedModels, setSelectedModels] = useState([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showDeleteSelectedDialog, setShowDeleteSelectedDialog] = useState(false);
  const [alert, setAlert] = useState(null);
  const [modelUpdated, setModelUpdated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // اعداد التوكن
  useEffect(() => {
    const token = sessionStorage.getItem("authToken");
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    }
  }, []);

  // جلب الموديلات + البراندات
  useEffect(() => {
    const fetchAll = async () => {
      setIsLoading(true);
      try {
        const [modelsRes, brandsRes] = await Promise.all([
          axios.get("https://laravel-api.aj-mart.com/api/accessoriesModel"),
          axios.get("https://laravel-api.aj-mart.com/api/brand"),
        ]);

        // الموديلات
        const modelsList =
          modelsRes.data?.models ||
          modelsRes.data?.data?.models ||
          modelsRes.data?.data ||
          modelsRes.data ||
          [];
        setModels(Array.isArray(modelsList) ? modelsList : []);

        // البراندات (الـ API المثال يرجّع { brands: [...] })
        const brandsList =
          brandsRes.data?.brands ||
          brandsRes.data?.data?.brands ||
          brandsRes.data?.data ||
          brandsRes.data ||
          [];
        setBrands(Array.isArray(brandsList) ? brandsList : []);

        // ماب للبحث السريع بالـ id
        const map = {};
        (Array.isArray(brandsList) ? brandsList : []).forEach((b) => {
          if (b?.brand_id != null) map[b.brand_id] = b;
        });
        setBrandsById(map);
      } catch (error) {
        console.error("Error fetching data:", error);
        setAlert({ type: "error", message: "Error fetching models/brands" });
      } finally {
        setIsLoading(false);
      }
    };

    fetchAll();
  }, [modelUpdated]);

  // إضافة موديل
  const handleSubmit = async (event) => {
    event.preventDefault();
    const form = event.target;
    const model_name = form.model_name.value?.trim();
    const brand_id = Number(form.brand_id.value || 0);

    if (!model_name) {
      setAlert({ type: "error", message: "Model name is required" });
      return;
    }
    if (!brand_id) {
      setAlert({ type: "error", message: "Please select a brand" });
      return;
    }

    try {
      const res = await axios.post(
        "https://laravel-api.aj-mart.com/api/accessoriesModel/add",
        { model_name, brand_id },
        { headers: { "Content-Type": "application/json" } }
      );

      // السيرفر قد يرجع الشكل بعدّة طرق؛ ناخذ أقرب واحدة
      const created =
        res.data?.model ||
        res.data?.data?.model ||
        res.data?.data ||
        res.data;

      if (created?.model_id) {
        setModels((prev) => [...prev, created]);
        setAlert({ type: "success", message: "Model added successfully" });
        form.reset();
      } else {
        // لو ما رجّعنا كائن الموديل كامل، نطلب ريفرش
        setAlert({ type: "success", message: "Model added successfully" });
        setModelUpdated((p) => !p);
        form.reset();
      }
    } catch (error) {
      console.error("Error adding model:", error);
      setAlert({ type: "error", message: "Error adding model" });
    }
  };

  // فتح نافذة التعديل
  const handleEditClick = (data) => {
    setEditData({
      model_id: data.model_id,
      model_name: data.model_name,
      // إن لم يكن موجودًا نضبطه على 0 ليتطلب اختيار
      brand_id: data.brand_id ?? 0,
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditData({});
  };

  // حفظ التعديلات
  const handleSaveChanges = async () => {
    if (!editData || !editData.model_id) {
      setAlert({ type: "error", message: "No model selected for updating." });
      return;
    }
    if (!editData.model_name?.trim()) {
      setAlert({ type: "error", message: "Model name is required." });
      return;
    }
    const brandIdNum = Number(editData.brand_id || 0);
    if (!brandIdNum) {
      setAlert({ type: "error", message: "Please select a brand." });
      return;
    }

    try {
      const response = await axios.put(
        `https://laravel-api.aj-mart.com/api/accessoriesModel/update/${editData.model_id}`,
        { model_name: editData.model_name.trim(), brand_id: brandIdNum },
        { headers: { "X-HTTP-Method-Override": "PUT" } }
      );

      if (response.data?.message?.toLowerCase?.().includes("updated")) {
        setAlert({ type: "success", message: "Model updated successfully" });
        // نحدّث محليًا بدون ريفرش كامل:
        setModels((prev) =>
          prev.map((m) =>
            m.model_id === editData.model_id
              ? { ...m, model_name: editData.model_name.trim(), brand_id: brandIdNum }
              : m
          )
        );
        handleCloseModal();
      } else {
        // بعض الـ APIs لا ترجع رسالة ثابتة — نعمل توغل لنعيد الجلب
        setAlert({ type: "success", message: "Model updated successfully" });
        setModelUpdated((p) => !p);
        handleCloseModal();
      }
    } catch (error) {
      console.error("Error updating model:", error);
      setAlert({ type: "error", message: "An unexpected error occurred." });
    }
  };

  // اختيار فردي
  const handleSelectModel = (model_id) => {
    setSelectedModels((prevSelected) =>
      prevSelected.includes(model_id)
        ? prevSelected.filter((id) => id !== model_id)
        : [...prevSelected, model_id]
    );
  };

  // اختيار الكل
  const handleSelectAll = () => {
    if (selectedModels.length === models.length && models.length > 0) {
      setSelectedModels([]);
    } else {
      setSelectedModels(models.map((model) => model.model_id));
    }
  };

  // حذف عنصر واحد
  const handleDeleteModel = (model_id) => {
    setShowDeleteDialog({ model_id });
  };

  const confirmDeleteModel = async (model_id) => {
    try {
      await axios.delete(
        `https://laravel-api.aj-mart.com/api/accessoriesModel/delete/${model_id}`
      );
      setModels((prev) => prev.filter((model) => model.model_id !== model_id));
      setAlert({ type: "success", message: "Model deleted successfully" });
      setShowDeleteDialog(false);
      setSelectedModels((prev) => prev.filter((id) => id !== model_id));
    } catch (error) {
      console.error("Error deleting model:", error);
      setAlert({ type: "error", message: "Error deleting model" });
      setShowDeleteDialog(false);
    }
  };

  // حذف متعدد
  const handleDeleteSelectedModels = () => {
    if (selectedModels.length === 0) {
      setAlert({
        type: "error",
        message: "Please select at least one model to delete",
      });
      return;
    }
    setShowDeleteSelectedDialog(true);
  };

  const confirmDeleteSelectedModels = async () => {
    try {
      await Promise.all(
        selectedModels.map((model_id) =>
          axios.delete(
            `https://laravel-api.aj-mart.com/api/accessoriesModel/delete/${model_id}`
          )
        )
      );
      setModels((prev) =>
        prev.filter((model) => !selectedModels.includes(model.model_id))
      );
      setAlert({
        type: "success",
        message: "Selected models deleted successfully",
      });
      setSelectedModels([]);
      setShowDeleteSelectedDialog(false);
    } catch (error) {
      console.error("Error deleting models:", error);
      setAlert({ type: "error", message: "Error deleting models" });
      setShowDeleteSelectedDialog(false);
    }
  };

  return (
    <section className="py-[25px]">
      {alert && (
        <CustomAlert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}

      <div className="main-title mb-[20px]">Manage Models</div>

      {/* إضافة موديل */}
      <div className="mb-[30px]">
        <form onSubmit={handleSubmit}>
          <div className="flex flex-col gap-[10px]">
            <div className="flex items-center gap-[10px]">
              <div className="flex flex-col gap-[5px] w-[49%]">
                <label className="text-black">Model Name</label>
                <input
                  name="model_name"
                  type="text"
                  className="px-[15px] py-[7px] outline-none border-2 border-[#1485a6] rounded-[4px] text-black bg-[#eeeeee6e]"
                  placeholder="Model Name"
                  required
                />
              </div>

              <div className="flex flex-col gap-[5px] w-[49%]">
                <label className="text-black">Brand</label>
                <select
                  name="brand_id"
                  className="px-[15px] py-[7px] outline-none border-2 border-[#1485a6] rounded-[4px] text-black bg-[#eeeeee6e]"
                  defaultValue=""
                  required
                >
                  <option value="" disabled>
                    Select Brand
                  </option>
                  {brands.map((b) => (
                    <option key={b.brand_id} value={b.brand_id}>
                      {b.brand_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <button type="submit" className="btn flex items-center gap-[5px]">
              <AddIcon />
              <span>Add Model</span>
            </button>
          </div>
        </form>
      </div>

      {/* أدوات جماعية */}
      <div className="flex justify-end items-center gap-[10px] capitalize mb-[20px]">
        <button
          className="btn bg-red-500 hover:bg-red-600 flex items-center gap-2"
          onClick={handleDeleteSelectedModels}
          disabled={!selectedModels.length}
        >
          <TrashWhite />
          <span>Delete Selected Models ({selectedModels.length})</span>
        </button>
      </div>

      {/* الجدول */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse border border-gray-200 text-left">
          <thead className="bg-gray-100">
            <tr>
              <th className="border border-gray-200 p-[10px] capitalize">
                <input
                  type="checkbox"
                  className="w-[18px] h-[18px]"
                  checked={models.length > 0 && selectedModels.length === models.length}
                  onChange={handleSelectAll}
                />
              </th>
              <th className="border border-gray-200 p-[10px] capitalize">Model Name</th>
              <th className="border border-gray-200 p-[10px] capitalize">Brand</th>
              <th className="border border-gray-200 p-[10px] capitalize">Actions</th>
            </tr>
          </thead>
          <tbody>
            {!isLoading && models.length === 0 && (
              <tr>
                <td className="border border-gray-200 p-[10px]" colSpan={4}>
                  No models found
                </td>
              </tr>
            )}
            {isLoading && (
              <tr>
                <td className="border border-gray-200 p-[10px]" colSpan={4}>
                  Loading...
                </td>
              </tr>
            )}
            {models.map((model) => (
              <tr key={model.model_id} className="bg-white hover:bg-gray-50">
                <td className="border border-gray-200 p-[10px]">
                  <input
                    type="checkbox"
                    className="w-[18px] h-[18px]"
                    checked={selectedModels.includes(model.model_id)}
                    onChange={() => handleSelectModel(model.model_id)}
                  />
                </td>
                <td className="border border-gray-200 p-[10px]">{model.model_name}</td>
                <td className="border border-gray-200 p-[10px]">
                  {brandsById[model.brand_id]?.brand_name ??
                    (model.brand_id ? `#${model.brand_id}` : "—")}
                </td>
                <td className="border border-gray-200 p-[10px]">
                  <div className="flex items-center gap-3">
                    <button
                      className="cursor-pointer"
                      onClick={() => handleEditClick(model)}
                      title="Edit"
                      type="button"
                    >
                      <EditIcon />
                    </button>
                    <button
                      className="cursor-pointer"
                      onClick={() => handleDeleteModel(model.model_id)}
                      title="Delete"
                      type="button"
                    >
                      <Trash />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="py-[25px]">
        <Pagination />
      </div>

      {/* Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white w-[500px] rounded-lg shadow-lg p-[20px] relative">
            <h2 className="text-xl font-bold mb-[10px]">Edit Model</h2>
            <div className="flex flex-col gap-[10px] capitalize">
              <label className="text-sm font-medium">
                Model Name
                <input
                  type="text"
                  className="block w-full mt-1 p-[10px] capitalize border rounded"
                  value={editData.model_name || ""}
                  onChange={(e) =>
                    setEditData({ ...editData, model_name: e.target.value })
                  }
                  required
                />
              </label>

              <label className="text-sm font-medium">
                Brand
                <select
                  className="block w-full mt-1 p-[10px] border rounded"
                  value={editData.brand_id || 0}
                  onChange={(e) =>
                    setEditData({ ...editData, brand_id: Number(e.target.value) })
                  }
                  required
                >
                  <option value={0} disabled>
                    Select Brand
                  </option>
                  {brands.map((b) => (
                    <option key={b.brand_id} value={b.brand_id}>
                      {b.brand_name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="flex justify-end gap-3 mt-5">
              <button
                className="btn bg-gray-300 hover:bg-gray-400"
                onClick={handleCloseModal}
                type="button"
              >
                Cancel
              </button>
              <button
                className="btn bg-blue-500 hover:bg-blue-600"
                onClick={handleSaveChanges}
                type="button"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white w-[400px] rounded-lg shadow-lg p-[20px] relative">
            <h2 className="text-xl font-bold mb-[10px]">Confirm Deletion</h2>
            <p className="text-sm mb-[20px]">
              Are you sure you want to delete this model?
            </p>
            <div className="flex justify-end gap-3">
              <button
                className="btn bg-gray-300 hover:bg-gray-400"
                onClick={() => setShowDeleteDialog(false)}
                type="button"
              >
                Cancel
              </button>
              <button
                className="btn bg-red-500 hover:bg-red-600"
                onClick={() => confirmDeleteModel(showDeleteDialog.model_id)}
                type="button"
              >
                Confirm Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Selected Confirmation Dialog */}
      {showDeleteSelectedDialog && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white w-[400px] rounded-lg shadow-lg p-[20px] relative">
            <h2 className="text-xl font-bold mb-[10px]">Confirm Deletion</h2>
            <p className="text-sm mb-[20px]">
              Are you sure you want to delete the selected models?
            </p>
            <div className="flex justify-end gap-3">
              <button
                className="btn bg-gray-300 hover:bg-gray-400"
                onClick={() => setShowDeleteSelectedDialog(false)}
                type="button"
              >
                Cancel
              </button>
              <button
                className="btn bg-red-500 hover:bg-red-600"
                onClick={confirmDeleteSelectedModels}
                type="button"
              >
                Confirm Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
